import React from "react";
import { createStyles, Theme, makeStyles } from "@material-ui/core/styles";
import {
  Paper,
  Button,
  CircularProgress,
  Grid,
} from "@material-ui/core";
import { useSelector } from "react-redux";
import { CustomSnackBarProps } from "../../../interfaces/common/CustomSnackBarProps";
import { useForm, FormContext, FieldError } from "react-hook-form";
import CustomSnackbar from "../../../components/notifications/CustomSnackbar";
import ProgressSaveButton from "../../../components/_Layout/Buttons/ProgressSaveButton";
import AccessManager from "../../../components/security/AccessManager";
import { security } from "../../../constants/Security/securityConstants";
import StateBoardStatesAndAccreditingAgenciesAutoComplete from "../../../components/AutoComplete/StateBoardStatesAndAccreditingAgenciesAutoComplete";
import StateBoardReportsAutoComplete from "../../../components/AutoComplete/StateBoardReportsAutoComplete";
import MultiCampusAutoComplete from "../../../components/AutoComplete/MultiCampusAutoComplete";
import IndianaStateBoardReportSetup from "./IndianaStateBoardReportSetup";
import MichiganStateBoardReportSetup from "./MichiganStateBoardReportSetup";
import PennsylvaniaStateBoardReportSetup from "./PennsylvaniaStateBoardReportSetup";
import TennesseeStateBoardReportSetup from "./TennesseeStateBoardReportSetup";
import NaccasAgencyReportSetup from "./NaccasAgencyReportSetup";
import COEAgencyReportSetup from "./COEAgencyReportSetup";


const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: "100%",
      height: "100%",
      minHeight: "100%",
      padding: theme.spacing(3),
      overflowX: "hidden",
      overflowY: "auto",
    },
    container: {
      display: "flex",
      flexDirection: "column",
      justifyContent: "flex-start",
      height: "100%",
    },
    details: {
      padding: theme.spacing(2, 0.5),
      flex: "1 1",
    },
    componentActions: {
      marginTop: theme.spacing(3),
    },
    wrapper: {
      height: "90%",
      padding: "3px",
    },
    form: {
      height: "100%",
    },
    titleFont: {
      background: "	rgb(220,220,220)",
      padding: "15px",
      textAlign: "left",
      fontFamily: "sans-serif",
    },
  })
);


const StateBoardReportsSetup = () => {
  const userSelectedCampus: string = useSelector((state: any) =>
    state.userstate.getSelectedCampus(state.session.user.userId)
  );

  const [snackBarProps, setSnackBarProps] = React.useState<CustomSnackBarProps>(
    {
      showSnackBar: false,
      messageInfo: undefined,
      variant: "info",
    }
  );

  const handleSave = async () => {
    setLoaderState(true);
    try {

    } catch (error) {
      setSnackBarProps((props: any) => {
        return {
          variant: "success",
          showSnackBar: true,
          messageInfo: "Error saving data. Please try again!",
        };
      });
    }
    setLoaderState(false);
  };

  const classes = useStyles({});

  const indianaStateDescription = 'Indiana';
  const indianaStateBoardReport = 'State Board Report';

  const michiganStateDescription = 'Michigan';
  const michiganStateBoardReportCosmetology = 'Cosmetology State Board Report';
  const michiganStateBoardReportBarber = 'Barber State Board Report';

  const pennsylvaniaStateDescription = 'Pennsylvania';
  const pennsylvaniaStateBoardReport = 'State Board Report';

  const tennesseeStateDescription = 'Tennessee';
  const tennesseeStateBoardReport = 'State Board Report';

  const naccasAgencyDescription = 'NACCAS';
  const coeAgencyDescription = 'COE';

  const [loaderState, setLoaderState] = React.useState<boolean>(false);
  const [selectedStateOrAgencyId, setSelectedStateOrAgencyId] = React.useState<string>();
  const [selectedStateOrAgency, setSelectedStateOrAgency] = React.useState<string>();
  const [selectedReportId, setSelectedReportId] = React.useState<string>();
  const [selectedReport, setSelectedReport] = React.useState<string>();
  const [campusId, setCampusId] = React.useState<string>();

  const selectedEnrollment = useSelector(
    (state: any) => state.student.selectedEnrollment
  );
  const data = useForm<any>({
    mode: "onBlur",
  });

  const showStateBoardReportType = React.useMemo(() => {
    return !!selectedStateOrAgency && selectedStateOrAgency.trim() != '' && (selectedStateOrAgency == indianaStateDescription || selectedStateOrAgency == michiganStateDescription || selectedStateOrAgency == pennsylvaniaStateDescription || selectedStateOrAgency == tennesseeStateDescription);
  }, [selectedStateOrAgency]);

  const isIndianaStateBoardSelected = React.useMemo(() => {
    return selectedStateOrAgency == indianaStateDescription && selectedReport == indianaStateBoardReport;
  },[selectedReport, selectedStateOrAgency]);

  const isMichiganSelected = React.useMemo(() => {
    return selectedStateOrAgency == michiganStateDescription && (selectedReport == michiganStateBoardReportBarber || selectedReport == michiganStateBoardReportCosmetology)
  },[selectedReport, selectedStateOrAgency]);

  const isPennsylvaniaSelected = React.useMemo(() => {
    return selectedStateOrAgency == pennsylvaniaStateDescription && selectedReport == pennsylvaniaStateBoardReport;
  },[selectedReport, selectedStateOrAgency]);

  const isTennesseeSelected = React.useMemo(() => {
    return selectedStateOrAgency == tennesseeStateDescription && selectedReport == tennesseeStateBoardReport;
  },[selectedReport, selectedStateOrAgency]);

  const isNaccassSelected = React.useMemo(() => {
    return selectedStateOrAgency == naccasAgencyDescription;
  },[selectedStateOrAgency]);

  const isCOESelected = React.useMemo(() => {
    return selectedStateOrAgency == coeAgencyDescription;
  },[selectedStateOrAgency]);

  const handleStateOrAgencyChange = (stateId: string, stateDescription: string) => {
    setSelectedStateOrAgency(stateDescription);
    setSelectedStateOrAgencyId(stateId);
    setSelectedReport(undefined);
    setSelectedReportId(undefined);
  }


  return (
    <Paper className={classes.root}>
      <CustomSnackbar
        variant={snackBarProps.variant}
        message={snackBarProps.messageInfo}
        open={snackBarProps.showSnackBar}
        onClose={(event?: React.SyntheticEvent, reason?: string) => {
          setSnackBarProps((props: any) => {
            return { ...props, showSnackBar: false };
          });
        }}
      ></CustomSnackbar>
      <FormContext {...data}>
        <Grid
              container
              direction="row"
              justify="flex-start"
              alignItems="flex-start"
              spacing={1}
            >
              <Grid item xs={12} sm={4} md={4}>
                <StateBoardStatesAndAccreditingAgenciesAutoComplete
                  disabled={false}
                  name='stateboardstateandaccreditingagencyid'
                  error={!!data.errors.stateboardstateandaccreditingagencyid}
                  inputRef={data.register({ required: "required" })}
                  helperText={
                    data.errors.stateboardstateandaccreditingagencyid
                      ? (data.errors.stateboardstateandaccreditingagencyid as FieldError).message
                      : undefined
                  }
                  valueFilter={
                    selectedStateOrAgencyId
                      ? {
                        key: 'value',
                        values: [selectedStateOrAgencyId]
                      }
                      : undefined
                  }
                  filterHandle={(v: any) => {
                    handleStateOrAgencyChange(v ? v.value : undefined, v ? v.text : undefined);
                  }}
                />
              </Grid>
              {showStateBoardReportType && <Grid item xs={12} sm={4} md={4}>
                <StateBoardReportsAutoComplete
                  disabled={false}
                  label="Report Type *"
                  name='reportTypeid'
                  params={{
                    stateId: selectedStateOrAgencyId
                  }}
                  error={!!data.errors.reportTypeid}
                  inputRef={data.register({ required: "Report type is required." })}
                  helperText={
                    data.errors.reportTypeid
                      ? (data.errors.reportTypeid as FieldError).message
                      : undefined
                  }
                  valueFilter={
                    selectedReportId
                      ? {
                        key: 'value',
                        values: [selectedReportId]
                      }
                      : undefined
                  }
                  filterHandle={(v: any) => {
                    setSelectedReportId(v ? v.value : null);
                    setSelectedReport(v ? v.text : null);
                  }}
                />
              </Grid>}
              <Grid item xs={12} sm={4} md={4}>
                <MultiCampusAutoComplete label="Campus *" multiple={false}
                  id="campusId"
                  name="campusId"
                  valueFilter={
                    campusId
                      ? {
                        key: "value",
                        values: [campusId],
                      }
                      : null
                  }
                  filterHandle={(v: any) => {
                    setCampusId(v.value);//for all the value is "All"
                  }}
                  error={!!data.errors.campusId}
                  inputRef={data.register({ required: "Campus is required." })}
                  helperText={
                    data.errors.campusId
                      ? (data.errors.campusId as FieldError).message
                      : undefined
                  }
                  includeAllValue={true}
                />
              </Grid></Grid>
                {isIndianaStateBoardSelected && <IndianaStateBoardReportSetup
                  campusId={campusId ?? ""}
                  stateBoardReportDescription={selectedReport ?? ""}
                  stateBoardReportId={selectedReportId ?? ""}
                  stateDescription={selectedStateOrAgency ?? ""}
                  stateId={selectedStateOrAgencyId ?? ""}
                />}
                {isMichiganSelected && <MichiganStateBoardReportSetup
                  campusId={campusId ?? ""}
                  stateBoardReportDescription={selectedReport ?? ""}
                  stateBoardReportId={selectedReportId ?? ""}
                  stateDescription={selectedStateOrAgency ?? ""}
                  stateId={selectedStateOrAgencyId ?? ""}
                />}
                {isPennsylvaniaSelected && <PennsylvaniaStateBoardReportSetup
                  campusId={campusId ?? ""}
                  stateBoardReportDescription={selectedReport ?? ""}
                  stateBoardReportId={selectedReportId ?? ""}
                  stateDescription={selectedStateOrAgency ?? ""}
                  stateId={selectedStateOrAgencyId ?? ""}
                />}
                {isTennesseeSelected && <TennesseeStateBoardReportSetup
                  campusId={campusId ?? ""}
                  stateBoardReportDescription={selectedReport ?? ""}
                  stateBoardReportId={selectedReportId ?? ""}
                  stateDescription={selectedStateOrAgency ?? ""}
                  stateId={selectedStateOrAgencyId ?? ""}
                />}
                {isNaccassSelected && <NaccasAgencyReportSetup
                  campusId={campusId ?? ""}
                  agencyId={selectedStateOrAgencyId ?? ""}
                  agencyDescription={selectedStateOrAgency ?? ""}
                />}
                {isCOESelected && <COEAgencyReportSetup
                  campusId={campusId ?? ""}
                  agencyId={selectedStateOrAgencyId ?? ""}
                  agencyDescription={selectedStateOrAgency ?? ""}
                />}
      </FormContext>
    </Paper>
  );
};

export default StateBoardReportsSetup;
